// StartupAnnDialog.tsx

import type React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
    StartupAnnConfig,
    Lang,
} from "../../../logic/helpers/startupAnns/startupAnns.config";
import "./StartupAnnDialog.scss";
import { useSlice } from "@bridge-applications/provider-kit";
import FatXmarkIcon from "../../../assets/icons/fat-xmark.svg?react";

interface StartupAnnDialogProps {
    ann: StartupAnnConfig | null;
    onClose: () => void;
}

/**
 * Full-screen backdrop + centered image.
 * - Click on backdrop => closes dialog.
 * - Click on image => runs switch(id) and THEN closes.
 */
export default function StartupAnnDialog({
    ann,
    onClose,
}: StartupAnnDialogProps) {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lang = i18n.language as Lang;
    const { state: feedState } = useSlice("feedSlice");

    // Nothing to render
    if (!ann) return null;

    // Resolve base image URL based on current language with fallbacks:
    // 1) exact language match
    // 2) "en"
    // 3) first available key
    const urls = ann.imageUrls;
    const langUrl = urls[lang];
    const enUrl = urls["en"];
    const firstKey = Object.keys(urls)[0] as keyof typeof urls | undefined;
    const fallbackUrl = firstKey ? urls[firstKey] : undefined;
    const baseSrc = langUrl ?? enUrl ?? fallbackUrl;

    if (!baseSrc) {
        // If misconfigured and no URL exists at all, don't render dialog.
        return null;
    }

    // Cloudflare Image Transform options:
    // - fit=scale-down: don't upscale, only shrink
    // - width=640: good max width for mobile dialogs
    // - quality=75: solid trade-off between size and quality
    // - format=auto: AVIF/WebP/JPEG depending on browser
    // - slow-connection-quality=50: lower quality on slow networks
    const CF_OPTIONS =
        "fit=scale-down,width=640,quality=75,format=auto,slow-connection-quality=50";

    const buildCfImageUrl = (original: string): string => {
        if (!original) return original;

        // Avoid double-wrapping if we ever store a transformed URL directly
        if (original.includes("/cdn-cgi/image/")) return original;

        try {
            const url = new URL(original);

            // Keep path + query + hash (in case you use them for versioning)
            const pathWithQuery =
                (url.pathname.startsWith("/")
                    ? url.pathname
                    : `/${url.pathname}`) +
                (url.search ?? "") +
                (url.hash ?? "");

            // For your R2 bucket domain on Cloudflare (media.bridge.cm),
            // use the path form:
            //   https://media.bridge.cm/cdn-cgi/image/<OPTIONS>/<PATH>
            return `${url.origin}/cdn-cgi/image/${CF_OPTIONS}${pathWithQuery}`;
        } catch {
            // If it's not a valid URL string, just return as-is
            return original;
        }
    };

    const src = buildCfImageUrl(baseSrc);

    if (!src) {
        return null;
    }

    const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = () => {
        onClose();
    };

    const handleContentClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        // Prevent backdrop click from firing when clicking on the content
        e.stopPropagation();
    };

    const handleImageClick: React.MouseEventHandler<HTMLImageElement> = (e) => {
        e.stopPropagation();

        // Switch on the announcement ID
        switch (ann.id) {
            case "referral-rewards-doubled":
            case "referral-rewards-doubled-v2":
                const referralItem = (feedState.items || []).find(
                    (item) => item.productType === "INTERNAL_CAMPAIGN_REF",
                );
                if (referralItem) {
                    navigate("/referral-v2", {
                        state: { campaignId: referralItem.itemId },
                    });
                }
                break;
            case "holidayCashback":
                // Example: navigate to some promo / spin page
                navigate("/spin");
                break;
            case "mw-free-coins":
                window.location.href =
                    "https://world.org/mini-app?app_id=app_cc9e631ad8d8936d6a4b34a403689279";
                break;
            default:
                // No-op for unknown IDs
                break;
        }

        // Close after handling click
        onClose();
    };

    return (
        <div
            className={`startup-ann-dialog__backdrop ${ann.id
                .toLowerCase()
                .replace(" ", "-")}`}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`startup-ann-dialog__content ${ann.id
                    .toLowerCase()
                    .replace(" ", "-")}`}
                onClick={handleContentClick}
            >
                <img
                    src={src}
                    alt="Announcement"
                    className="startup-ann-dialog__image"
                    onClick={handleImageClick}
                />

                <button
                    className="startup-ann-dialog__content__close-btn"
                    onClick={onClose}
                >
                    <FatXmarkIcon />
                </button>
            </div>
        </div>
    );
}
